import { Inject, Injectable, OnDestroy } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import {
  asyncScheduler,
  BehaviorSubject,
  fromEvent,
  Observable,
  of,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  share,
  takeUntil,
  throttleTime,
  withLatestFrom,
} from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { HttpRequest } from '@angular/common/http';
// import { Logger } from '@utils/logger';

// const logger = Logger.getLogger('WindowScrollService');

/**
 * This is the buffer height that counts as scrolled to page bottom
 */
const WINDOW_BOTTOM_BUFFER_HEIGHT = 20;

/**
 * Used to hold id and location of page section
 */
interface OrderedSection {
  /**
   * element id for the section
   */
  id: string;
  /**
   * distance between element top and page top
   */
  top: number;
}

export interface ComponentLoaded {
  overview: boolean;
  portfolio: boolean;
  performance: boolean;
  pricing: boolean;
  distributions: boolean;
  documents: boolean;
  ['press-releases']: boolean;
}

/**
 * A service that provides various helper methods for dealing with page scrolling
 */
@Injectable({
  providedIn: 'root',
})
export class WindowScrollService implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();
  private scrollObs$: Observable<Event>;
  private requests: HttpRequest<any>[] = [];
  private loadedSections: ComponentLoaded = {
    overview: false,
    portfolio: false,
    performance: false,
    pricing: false,
    distributions: false,
    documents: false,
    ['press-releases']: true,
  };

  public componentLoaded$ = new BehaviorSubject<ComponentLoaded>(
    this.loadedSections
  );

  /**
   * Stream of (throttled) window scroll events
   */
  scroll$: ReplaySubject<Event>;

  /**
   * NB: services only have ngOnDestroy() hook, so this will do setup work
   */
  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    @Inject(DOCUMENT) private documentRef: Document
  ) {
    this.scroll$ = new ReplaySubject();
    this.scrollObs$ = this.getScrollObs$(100); // throttles by 100ms
    this.scrollObs$.pipe(takeUntil(this.unsubscribe$)).subscribe(this.scroll$);
  }

  /**
   * Returns Observable from window scroll event
   * @param windowRef: Window object
   * @param throttleMs: throttle time in millisecs
   * @returns Observable<Event>
   */
  getScrollObs$(throttleMs: number): Observable<Event> {
    return fromEvent(this.windowRef, 'scroll').pipe(
      throttleTime(throttleMs, asyncScheduler, {
        leading: true,
        trailing: true,
      }),
      map((e): Event => e)
    );
  }

  addRequest(req: HttpRequest<any>) {
    this.requests.push(req);
  }

  private requestCheck() {
    const commonRequests = this.requests.findIndex(
      (req) =>
        req?.body?.operationName === 'Labels' ||
        req?.body?.operationName === 'ProductDetail'
    );
    const overviewRequests = this.requests.findIndex(
      (req) => req?.body?.operationName === 'FundSummary'
    );
    const portfolioRequests = this.requests.findIndex(
      (req) => req?.body?.operationName === 'PortfolioStatistics'
    );
    const performanceRequests = this.requests.findIndex(
      (req) =>
        req?.body?.operationName === 'Cumulative' ||
        req?.body?.operationName === 'Annualized' ||
        req?.body?.operationName === 'CalendarYear' ||
        req?.body?.operationName === 'Discrete' ||
        req?.body?.operationName === 'RiskStatsSummary' ||
        req?.body?.operationName === 'RiskReturnProfile'
    );
    const distributionsRequests = this.requests.findIndex(
      (req) => req?.body?.operationName === 'DistributionHistory'
    );
    const pricingRequests = this.requests.findIndex(
      (req) => req?.body?.operationName === 'PricesHistory'
    );
    const documentRequests = this.requests.findIndex(
      (req) => req?.body?.operationName === 'FundDocuments'
    );

    if (commonRequests !== -1) {
      this.loadedSections.overview = true;
      this.componentLoaded$.next(this.loadedSections);
    }
    if (
      commonRequests === -1 &&
      overviewRequests === -1 &&
      portfolioRequests === -1
    ) {
      this.loadedSections.portfolio = true;
      this.componentLoaded$.next(this.loadedSections);
    }
    if (
      commonRequests === -1 &&
      overviewRequests === -1 &&
      portfolioRequests === -1 &&
      performanceRequests === -1
    ) {
      this.loadedSections.performance = true;
      this.componentLoaded$.next(this.loadedSections);
    }
    if (
      commonRequests === -1 &&
      overviewRequests === -1 &&
      portfolioRequests === -1 &&
      performanceRequests === -1 &&
      distributionsRequests === -1
    ) {
      this.loadedSections.distributions = true;
      this.componentLoaded$.next(this.loadedSections);
    }
    if (
      commonRequests === -1 &&
      overviewRequests === -1 &&
      portfolioRequests === -1 &&
      performanceRequests === -1 &&
      distributionsRequests === -1 &&
      pricingRequests === -1
    ) {
      this.loadedSections.pricing = true;
      this.componentLoaded$.next(this.loadedSections);
    }
    if (
      commonRequests === -1 &&
      overviewRequests === -1 &&
      portfolioRequests === -1 &&
      performanceRequests === -1 &&
      distributionsRequests === -1 &&
      pricingRequests === -1 &&
      documentRequests === -1
    ) {
      this.loadedSections.documents = true;
      this.componentLoaded$.next(this.loadedSections);
    }
  }

  removeRequest(req: HttpRequest<any>) {
    const i = this.requests.indexOf(req);

    if (i >= 0) {
      this.requests.splice(i, 1);
    }
    setTimeout(() => {
      this.requestCheck();
    }, 10);
  }

  /**
   * unsubscribes from pageScroll service when service is destroyed
   */
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * indicates if page has scroled to the bottom
   */
  isPageBottom(): boolean {
    return (
      this.windowRef.innerHeight + this.windowRef.pageYOffset >=
      this.documentRef.body.offsetHeight - WINDOW_BOTTOM_BUFFER_HEIGHT
    );
  }

  /**
   * returns a stream with the distance scrolled from top of the page
   */
  getPageYOffset$(): Observable<number> {
    return this.scroll$.pipe(
      map((): number => this.windowRef.pageYOffset),
      share()
    );
  }

  /**
   * Watches an array of html elements, and returns a stream that updates as the user scrolls, indicating which element the user is
   * currently viewing
   * @param ids Array of html element ids
   * @param offset Space at top of screen to allow for when calculating the current section. E.g. height of a sticky header
   * @param suppressor$ observable that can suppress scroll events. Used to stop events firing when scrolling due to tab clicked
   */
  getCurrentSection$(
    ids: string[],
    offset: number = 0,
    suppressor$: Observable<boolean> = of(false)
  ): Observable<string> {
    // logger.debug('ids:', ids);
    return this.getPageYOffset$().pipe(
      withLatestFrom(suppressor$),
      filter(([pageTop, isSuppressed]: [number, boolean]): boolean => {
        // logger.debug('filter:', isSuppressed, pageTop);
        return !isSuppressed;
      }),
      map(([pageTop, isSuppressed]: [number, boolean]): string => {
        // recalculate sections each time
        // this may not be efficient, but they may not exist initially
        const sections: OrderedSection[] = ids
          .reduce((prev: OrderedSection[], id: string): OrderedSection[] => {
            const el: HTMLElement = this.documentRef.getElementById(id);
            if (el) {
              prev.push({
                id,
                top: el.offsetTop,
              });
            }
            return prev;
          }, [])
          .sort(
            (a: OrderedSection, b: OrderedSection): number => b.top - a.top
          ); // IMPORTANT: reverse order
        // logger.debug('sections:', isSuppressed, sections);
        if (sections.length < 1) {
          // no sections found on page, so return null
          return null;
        }
        if (this.isPageBottom()) {
          // return last section id
          return sections[0].id;
        }
        //  find intersecting section
        const section: OrderedSection = sections.find(
          (s: OrderedSection): boolean => s.top <= pageTop + offset
        );
        if (section) {
          // return intersecting section
          return section.id;
        }
        // we must be above the first section, so just return first section
        // remember the array order is reversed, so first on page is last in array
        return sections[sections.length - 1].id;
      }),
      distinctUntilChanged(),
      share()
    );
  }
}
